/**
 * GoalChecklistEditor — items editor for checklist-primitive goals
 * (DEV-1105).
 *
 * Lives inside the GoalEditTray when the chosen primitive is 'checklist'.
 * Replaces the (previously hidden) target field for this primitive —
 * for checklists, "completion" is derived from items_completed / items_total,
 * not from comparing a numeric value against a target.
 *
 * ── Item identity (locked design) ──
 *
 * Each item carries an explicit `position` field that anchors its identity.
 * mission_metric_points.item_id is derived as `mp_item_<position>` and never
 * renumbers when items are added, deleted, or reordered. This preserves the
 * historical link between completion events and the item they refer to —
 * deleting an item leaves its history intact but unreferenced, and re-adding
 * an item with a label similar to a deleted one does NOT inherit the
 * deleted item's history (new position = no historical match).
 *
 *   Add: appends with position = max(existing positions) + 1.
 *   Remove: deletes from the array. Position not reused.
 *   Reorder: array position changes; the `position` field stays the same.
 *
 * Array order = display order. Position = stable identity. The two are
 * intentionally decoupled.
 *
 * Backward compatibility: legacy rows without explicit position field are
 * read elsewhere with `def.position ?? idx` fallback (see
 * miniProjectGoalView.ts). This editor always writes positions, so once
 * a row is edited through this surface it carries explicit positions
 * forward.
 *
 * Scope (v1):
 *   - Add (button at bottom)
 *   - Edit label (text input per row)
 *   - Remove (× button per row)
 *   - Reorder via up/down buttons (no drag-drop in v1; keyboard-accessible
 *     and dependency-free)
 *
 * Out of scope (v1):
 *   - Drag-and-drop reorder (filed as polish if needed)
 *   - Per-item deadlines (Skip 2026-05-22: scrapped)
 *   - Per-item weight / required (over-engineered for v1)
 */

import React, { useCallback } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./GoalChecklistEditor.module.css";

/** Persisted JSONB shape (per item). label_key is the user-typed text
 *  (legacy naming — predates DEV-1075 — kept for shape stability). */
export interface ChecklistItem {
  label_key: string;
  /** Stable identity. Used to derive mp_item_<position> for
   *  mission_metric_points.item_id. Assigned at creation, never reused
   *  or renumbered. */
  position: number;
}

interface Props {
  items: ChecklistItem[];
  onChange: (next: ChecklistItem[]) => void;
  disabled?: boolean;
}

/** Inline icons matching the tray's icon weight. */
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const GoalChecklistEditor: React.FC<Props> = ({ items, onChange, disabled = false }) => {
  const { t } = useAppTranslation("pages");

  const nextPosition = useCallback((): number => {
    if (items.length === 0) return 0;
    return Math.max(...items.map((it) => it.position)) + 1;
  }, [items]);

  const handleAdd = useCallback(() => {
    onChange([...items, { label_key: "", position: nextPosition() }]);
  }, [items, nextPosition, onChange]);

  const handleRemove = useCallback(
    (idx: number) => {
      // Remove from array. Position is not reused — the gap is intentional
      // (preserves stability of mp_item_<position> for historical
      // mission_metric_points rows).
      onChange(items.filter((_, i) => i !== idx));
    },
    [items, onChange],
  );

  const handleLabelChange = useCallback(
    (idx: number, label: string) => {
      onChange(items.map((it, i) => (i === idx ? { ...it, label_key: label } : it)));
    },
    [items, onChange],
  );

  const handleMove = useCallback(
    (idx: number, direction: -1 | 1) => {
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= items.length) return;
      // Swap array positions. The `position` field stays attached to
      // each item — only the visual order changes.
      const next = [...items];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      onChange(next);
    },
    [items, onChange],
  );

  return (
    <div className={styles.editor}>
      {items.length === 0 ? (
        <div className={styles.empty}>
          {t("miniProject.cards.goal.checklist.emptyEditor")}
        </div>
      ) : (
        <ul className={styles.list}>
          {items.map((it, idx) => (
            <li key={`pos-${it.position}`} className={styles.item}>
              <div className={styles.itemReorder}>
                <button
                  type="button"
                  className={styles.reorderBtn}
                  onClick={() => handleMove(idx, -1)}
                  disabled={disabled || idx === 0}
                  aria-label={t("miniProject.cards.goal.checklist.moveUp")}
                  title={t("miniProject.cards.goal.checklist.moveUp")}
                >
                  <ArrowUpIcon />
                </button>
                <button
                  type="button"
                  className={styles.reorderBtn}
                  onClick={() => handleMove(idx, 1)}
                  disabled={disabled || idx === items.length - 1}
                  aria-label={t("miniProject.cards.goal.checklist.moveDown")}
                  title={t("miniProject.cards.goal.checklist.moveDown")}
                >
                  <ArrowDownIcon />
                </button>
              </div>
              <input
                type="text"
                className={styles.itemInput}
                value={it.label_key}
                onChange={(e) => handleLabelChange(idx, e.target.value)}
                placeholder={t("miniProject.cards.goal.checklist.itemPlaceholder")}
                disabled={disabled}
              />
              <button
                type="button"
                className={styles.itemRemove}
                onClick={() => handleRemove(idx)}
                disabled={disabled}
                aria-label={t("miniProject.cards.goal.checklist.removeItem")}
                title={t("miniProject.cards.goal.checklist.removeItem")}
              >
                <TrashIcon />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className={styles.addBtn}
        onClick={handleAdd}
        disabled={disabled}
      >
        <PlusIcon /> {t("miniProject.cards.goal.checklist.addItem")}
      </button>
    </div>
  );
};

export default GoalChecklistEditor;
